import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Mail, MapPin, Phone, Lock } from "lucide-react";
import { api } from "@/lib/api";
import NewsletterForm from "@/components/NewsletterForm";

export default function Footer() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <footer className="relative bg-navy text-cream/90 overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 pattern-girih opacity-[0.25]" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="grid lg:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img
                src="/fic-logo.png"
                alt="FIC"
                className="h-12 bg-cream/95 p-1 rounded"
              />
              <div>
                <div className="font-serif text-xl text-cream">Flint Islamic Center</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Est. Community of Flint</div>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-cream/70 font-light">
              A place of worship, learning, and service — dedicated to upholding the teachings of
              Islam exemplified by the Prophet Muhammad&nbsp;ﷺ.
            </p>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-gold mb-5">Visit</div>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3"><MapPin size={16} className="shrink-0 mt-0.5 text-gold" />
                <span>{settings?.address_line1 || "9447 Corunna Rd"}<br/>{settings?.address_line2 || "Swartz Creek, MI 48473"}</span>
              </li>
              <li className="flex gap-3"><Phone size={16} className="shrink-0 mt-0.5 text-gold" />
                <a href={`tel:${(settings?.phone || "+18104062617").replace(/\s|\(|\)|-/g, "")}`} className="hover:text-gold">{settings?.phone || "+1 (810) 406-2617"}</a>
              </li>
              <li className="flex gap-3"><Mail size={16} className="shrink-0 mt-0.5 text-gold" />
                <a href={`mailto:${settings?.email || "flintislamiccenter@gmail.com"}`} className="hover:text-gold">{settings?.email || "flintislamiccenter@gmail.com"}</a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-gold mb-5">Explore</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="hover:text-gold">About FIC</Link></li>
              <li><Link to="/services" className="hover:text-gold">Services</Link></li>
              <li><Link to="/events" className="hover:text-gold">Events</Link></li>
              <li><Link to="/prayer-times" className="hover:text-gold">Prayer Times</Link></li>
              <li><Link to="/donate" className="hover:text-gold">Donate</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-gold mb-5">Stay Connected</div>
            <p className="text-sm text-cream/70 mb-4 font-light">Get FIC announcements in your inbox.</p>
            <NewsletterForm dark />
            <div className="flex gap-3 mt-6">
              {settings?.facebook && (
                <a href={settings.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-cream/25 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy transition-all" data-testid="social-facebook">
                  <Facebook size={15} />
                </a>
              )}
              {settings?.instagram && (
                <a href={settings.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-cream/25 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy transition-all" data-testid="social-instagram">
                  <Instagram size={15} />
                </a>
              )}
              {settings?.youtube && (
                <a href={settings.youtube} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-cream/25 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy transition-all" data-testid="social-youtube">
                  <Youtube size={15} />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-cream/15 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-cream/55">
          <div className="flex items-center gap-2">
            <span>© {new Date().getFullYear()} Flint Islamic Center. All rights reserved.</span>
            <Link
              to="/admin/login"
              aria-label="Staff sign-in"
              title="Staff sign-in"
              data-testid="footer-admin-lock"
              className="inline-flex items-center justify-center w-5 h-5 rounded-full text-cream/25 hover:text-gold hover:bg-cream/5 transition-colors"
            >
              <Lock size={11} strokeWidth={2.2} />
            </Link>
          </div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-gold" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gold" data-testid="footer-terms">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
